import type { Hit, Sort } from '../api'
import type { View } from '../state'
import type { Group } from '../tree'
import { FIELD_LABELS, activeConditions, removeNode, valueText } from '../tree'

type Props = {
  hits: Hit[]
  total: number | undefined
  took?: number
  loading: boolean
  error: string | null
  warnings: string[]
  tree: Group
  setTree: (t: Group | ((p: Group) => Group)) => void
  sort: Sort
  setSort: (s: Sort) => void
  view: View
  setView: (v: View) => void
  hasMore: boolean
  loadMore: () => void
  loadingMore: boolean
  onOpen: (id: number) => void
  picked: Set<number>
  onPick: (h: Hit) => void
  toggleFacets: () => void
  isPublic: boolean
}

const SORTS: [Sort, string][] = [['relevance', 'Relevanz'], ['old', 'älteste zuerst'], ['new', 'neueste zuerst'], ['inv', 'Inventarnummer'], ['added', 'zuletzt erfasst']]

export function Results(p: Props) {
  const conds = activeConditions(p.tree)
  return (
    <section className="results" aria-live="polite">
      <div className="rhead">
        <div className="rcount">
          {p.total === undefined ? <span className="muted">Suche …</span> : <><b>{p.total.toLocaleString('de-DE')}</b> {p.total === 1 ? 'Objekt' : 'Objekte'}</>}
          {p.took !== undefined && <span className="muted took"> · {p.took} ms</span>}
        </div>
        <div className="rtools">
          <button className="facettoggle" onClick={p.toggleFacets}>Filter</button>
          <label>Sortierung{' '}
            <select id="sort" value={p.sort} onChange={(e) => p.setSort(e.target.value as Sort)}>
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <div className="seg" role="group" aria-label="Darstellung">
            {([['grid', 'Kontaktbogen'], ['list', 'Liste'], ['timeline', 'Zeitleiste']] as [View, string][]).map(([v, l]) => (
              <button key={v} aria-pressed={p.view === v} onClick={() => p.setView(v)}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      {p.loading && <div className="loadingbar" aria-hidden="true" />}
      {conds.length > 0 && (
        <div className="chips">
          {conds.map((c) => (
            <span key={c._id} className={`chip${c.not ? ' neg' : ''}`}>
              <span className="k">{c.not ? 'nicht ' : ''}{FIELD_LABELS[c.field] ?? c.field}</span>
              <span className="v">{valueText(c)}</span>
              <button className="x" aria-label="Entfernen" onClick={() => p.setTree((t) => removeNode(t, c._id!))}>×</button>
            </span>
          ))}
        </div>
      )}
      {p.error && <div className="banner" role="alert">{p.error}</div>}
      {p.warnings.map((w) => <div key={w} className="banner info">{w}. Diese Bedingung findet nichts.</div>)}
      {p.total === 0 && !p.loading && (
        <div className="none">
          <h3>Keine Objekte für diese Suche.</h3>
          <p className="muted">Entfernen Sie eine Bedingung, stellen Sie eine UND-Verknüpfung auf ODER um oder erweitern Sie den Zeitraum.
            {p.isPublic && ' Angemeldete Nutzer mit Recherche-Zugang sehen unter Umständen mehr.'}</p>
        </div>
      )}
      {p.hits.length > 0 && p.view === 'grid' && (
        <div className="sheet">{p.hits.map((h) => <Card key={h.id} h={h} onOpen={p.onOpen} picked={p.picked.has(h.id)} onPick={p.onPick} />)}</div>
      )}
      {p.hits.length > 0 && p.view === 'list' && <List hits={p.hits} onOpen={p.onOpen} />}
      {p.hits.length > 0 && p.view === 'timeline' && <Timeline hits={p.hits} onOpen={p.onOpen} />}
      {p.hasMore && p.hits.length > 0 && (
        <div className="pager"><button onClick={p.loadMore} disabled={p.loadingMore}>{p.loadingMore ? 'Lade …' : 'Weitere laden'}</button></div>
      )}
    </section>
  )
}

export function place(h: Hit) {
  const p = h.places[h.places.length - 1]
  return p ? p.label : ''
}

function Card({ h, onOpen, picked, onPick }: { h: Hit; onOpen: (id: number) => void; picked: boolean; onPick: (h: Hit) => void }) {
  const img = h.images.small
  const hi = h.images.preview
  return (
    <article className="card">
      <button className="pick" aria-pressed={picked} title="Auf den Leuchttisch" aria-label="Auswählen" onClick={() => onPick(h)}>{picked ? '✓' : '+'}</button>
      <div className="frame" role="button" tabIndex={0} aria-label={`${h.title} öffnen`} onClick={() => onOpen(h.id)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(h.id) } }}>
        {img ? <img src={img.url} srcSet={hi ? `${img.url} 1x, ${hi.url} 2x` : undefined} alt="" loading="lazy" />
          : <span className="noimg">kein Bild</span>}
        <span className="fno">{h.inv.split('-').pop()}</span>
      </div>
      <div className="cap"><div className="t" title={h.title} {...(h.title_html ? { dangerouslySetInnerHTML: { __html: h.title_html } } : { children: h.title })} /></div>
      <div className="meta">
        <span className="mono" title={`${h.inv}${place(h) ? ' · ' + place(h) : ''}`}>{h.date || '–'}{place(h) ? ` · ${place(h)}` : ''}</span>
        {h.licensable && <span className="badge L">Lizenz</span>}
      </div>
    </article>
  )
}

function List({ hits, onOpen }: { hits: Hit[]; onOpen: (id: number) => void }) {
  return (
    <div className="tablewrap">
      <table className="list">
        <thead><tr><th /><th>Inventarnr.</th><th>Titel</th><th>Datierung</th><th>Personen</th><th>Ort</th><th>Technik</th><th>Status</th></tr></thead>
        <tbody>
          {hits.map((h) => (
            <tr key={h.id} onClick={() => onOpen(h.id)}>
              <td>{h.images.small && <img src={h.images.small.url} alt="" loading="lazy" />}</td>
              <td className="mono">{h.inv}</td>
              <td>{h.title_html ? <span dangerouslySetInnerHTML={{ __html: h.title_html }} /> : h.title}</td>
              <td className="mono">{h.date}</td>
              <td>{h.persons.map((x) => x.label).join(', ')}</td>
              <td>{place(h)}</td>
              <td>{h.techniques.map((x) => x.label).join(', ')}</td>
              <td>{h.licensable && <span className="badge L">Lizenz</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Timeline({ hits, onOpen }: { hits: Hit[]; onOpen: (id: number) => void }) {
  const by = new Map<number | 'x', Hit[]>()
  for (const h of hits) {
    const d = h.year ? Math.floor(h.year / 10) * 10 : 'x'
    if (!by.has(d)) by.set(d, [])
    by.get(d)!.push(h)
  }
  const keys = [...by.keys()].sort((a, b) => (a === 'x' ? 1 : b === 'x' ? -1 : (a as number) - (b as number)))
  return (
    <>
      <p className="note">Zeitleiste der geladenen Treffer. Für eine vollständige Zeitleiste nach Jahr sortieren und weitere laden.</p>
      {keys.map((d) => (
        <div className="decade" key={String(d)}>
          <h4>{d === 'x' ? 'o. J.' : `${d}er`}<small>{by.get(d)!.length} Objekte</small></h4>
          <div className="strip">
            {by.get(d)!.sort((a, b) => (a.year ?? 0) - (b.year ?? 0)).map((h) => (
              <button key={h.id} onClick={() => onOpen(h.id)} title={`${h.title} (${h.date})`}>
                {h.images.small && <img src={h.images.small.url} alt={h.title} loading="lazy" />}
                <span>{h.year ?? ''}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}
